/**
 * Workspace initials used by the collapsed left-nav rail.
 *
 * Splits by whitespace, keeps tokens that begin with a letter or digit, and
 * returns the uppercased first character of up to three such tokens. Returns
 * an empty string for blank names so callers can safely fall back to an icon.
 */
export const workspaceInitials = (name: string): string => {
  return name
    .split(/\s+/u)
    .map((word) => word.charAt(0))
    .filter((char) => /[\p{L}\p{N}]/u.test(char))
    .slice(0, 3)
    .join("")
    .toUpperCase();
};

/** Width assumed for a workspace tab that has not been measured yet. */
export const ESTIMATED_TAB_WIDTH = 140;

/** Width reserved for the `More` trigger once anything has to collapse. */
export const MORE_TRIGGER_WIDTH = 92;

export interface NavOverflowInput<T extends { id: string }> {
  tabs: ReadonlyArray<T>;
  /** Horizontal space the tab strip occupies, in CSS pixels. */
  availableWidth: number;
  /** Measured tab widths keyed by tab id; misses fall back to the estimate. */
  measuredWidths: Readonly<Record<string, number>>;
  /** Strip space workspace tabs cannot use, i.e. the pinned Overview tab. */
  reservedWidth?: number;
  /** Kept visible even when it would not fit by packing order. */
  activeTabId?: string | null;
}

export interface NavOverflowResult<T> {
  visibleTabs: ReadonlyArray<T>;
  overflowTabs: ReadonlyArray<T>;
}

/**
 * Packs workspace tabs into the available width, moving the remainder into the
 * `More` menu. A non-positive width means nothing has been measured yet (jsdom,
 * first paint), so every tab stays visible rather than collapsing spuriously.
 *
 * The active tab is retained whether or not it fits by packing order, so the
 * open workspace can never hide behind the menu. It displaces as few of the
 * packed tabs as will free the room, and since it always follows them it lands
 * last in the visible run — the strip trades a gap in the sequence for keeping
 * the current destination on screen.
 */
export const computeNavOverflow = <T extends { id: string }>({
  tabs,
  availableWidth,
  measuredWidths,
  reservedWidth = 0,
  activeTabId,
}: NavOverflowInput<T>): NavOverflowResult<T> => {
  if (availableWidth <= 0) {
    return { visibleTabs: tabs, overflowTabs: [] };
  }

  const widthOf = (tab: T) => measuredWidths[tab.id] ?? ESTIMATED_TAB_WIDTH;
  const space = Math.max(0, availableWidth - reservedWidth);
  const totalWidth = tabs.reduce((sum, tab) => sum + widthOf(tab), 0);
  if (totalWidth <= space) {
    return { visibleTabs: tabs, overflowTabs: [] };
  }

  const budget = space - MORE_TRIGGER_WIDTH;
  const visibleIds = new Set<string>();
  let used = 0;
  for (const tab of tabs) {
    const next = used + widthOf(tab);
    if (next > budget) {
      break;
    }
    used = next;
    visibleIds.add(tab.id);
  }

  const activeTab = activeTabId
    ? tabs.find((tab) => tab.id === activeTabId)
    : undefined;
  if (activeTab && !visibleIds.has(activeTab.id)) {
    // Drop packed tabs from the end until the active tab fits beside them.
    const packed = tabs.filter((tab) => visibleIds.has(tab.id));
    let reclaimed = used;
    for (let index = packed.length - 1; index >= 0; index -= 1) {
      if (reclaimed + widthOf(activeTab) <= budget) {
        break;
      }
      const dropped = packed[index];
      visibleIds.delete(dropped.id);
      reclaimed -= widthOf(dropped);
    }
    visibleIds.add(activeTab.id);
  }

  return {
    visibleTabs: tabs.filter((tab) => visibleIds.has(tab.id)),
    overflowTabs: tabs.filter((tab) => !visibleIds.has(tab.id)),
  };
};
